import React, { useState, useEffect } from 'react';
import { ApiRequest, ToastBuilder, ButtonBuilder, TableBuilder, ModalButtonBuilder } from '../../../../../../../../GlobalComponents/Builders';
import { showTimestamp, jsonPre, handleCopy } from '../../../../../../../../GlobalComponents/Helpers';
import { Copy, Trash2 } from 'react-feather';

const IsolatedLogViewer = ({ jobId, resourceId, sourceRecId }) => {

    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchLogs = async () => {
        setIsLoading(true);
        const queryParams = {};
        if (resourceId) queryParams.resource_id = resourceId;
        if (sourceRecId) queryParams.source_rec_id = sourceRecId;
        queryParams.orderBy = 'created_at DESC';

        const res = await ApiRequest('GET', '/dmt/jobs/{jobId}/isolatedLogs', {
            pathParams: { jobId },
            queryParams,
        });
        if (res.success && res.data) {
            setLogs(Array.isArray(res.data) ? res.data : []);
        } else {
            setLogs([]);
        }
        setIsLoading(false);
    };

    const deleteLog = async (logId) => {
        const res = await ApiRequest('DELETE', '/dmt/jobs/{jobId}/isolatedLogs/{logId}', {
            pathParams: { jobId, logId },
            queryParams: { resource_id: resourceId },
        });
        if (res.success) {
            ToastBuilder('success', 'Log deleted');
            setLogs(prev => prev.filter(l => l.id !== logId));
        } else {
            ToastBuilder('error', res.error || 'Failed to delete log');
        }
    };

    useEffect(() => {
        if (jobId) fetchLogs();
    }, [jobId, resourceId, sourceRecId]);

    const renderLogData = (row) => {
        let parsed = row.log_data;
        if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch { /* keep as string */ }
        }

        if (Array.isArray(parsed)) {
            return (
                <div style={{ maxHeight: '70vh', overflow: 'auto', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre', padding: 16, borderRadius: 6 }} className='section-group-muted'>
                    {parsed.map((line, i) => {
                        if (typeof line === 'object') {
                            return <div key={i} style={{ marginBottom: 4, color: '#666' }}>{JSON.stringify(line, null, 2)}</div>;
                        }
                        const isError = typeof line === 'string' && (line.includes('ERROR') || line.includes('FAILED'));
                        return <div key={i} style={{ color: isError ? '#dc3545' : 'inherit', marginBottom: 1 }}>{String(line)}</div>;
                    })}
                </div>
            );
        }

        return jsonPre(parsed);
    };

    if (isLoading) return <p style={{ fontSize: 12, color: '#999' }}>Loading isolated logs...</p>;

    if (logs.length === 0) return (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: 13, color: '#999' }}>No isolated logs found for this record.</p>
            <p style={{ fontSize: 11, color: '#bbb' }}>Queue the record with "Isolate Logs" enabled to capture processor output here.</p>
        </div>
    );

    return (
        <div>
            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{logs.length} Isolated Log{logs.length !== 1 ? 's' : ''}</span>
                <ButtonBuilder label='Refresh' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }}
                    onClick={fetchLogs} useSpinner={false} />
            </div>

            {logs.map((logEntry, idx) => (
                <div key={logEntry.id} className='section-group-muted' style={{ borderRadius: 6, padding: '12px 16px', marginBottom: 10 }}>
                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                        <div className='d-flex align-items-center' style={{ gap: 10 }}>
                            <span style={{ fontWeight: 600, fontSize: 12, color: logEntry.status === 'error' ? '#dc3545' : '#198754' }}>
                                {logEntry.status.toUpperCase()}
                            </span>
                            <span style={{ fontSize: 11, color: '#999' }}>{showTimestamp(logEntry.created_at * 1000)}</span>
                            <span style={{ fontSize: 11, color: '#bbb', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {logEntry.execution_identifier}
                            </span>
                        </div>
                        <div className='d-flex gap-1'>
                            <ModalButtonBuilder
                                title='View Full Log'
                                className='btn-outline-primary btn-sm m-0'
                                onSubmit={false}
                                onCancelLabel='Close'
                                size='xl'
                                body={renderLogData(logEntry)}
                            />
                            <ButtonBuilder label={<Trash2 size={12} />} className='btn-outline-danger btn-sm'
                                style={{ padding: '2px 6px' }}
                                onClick={() => deleteLog(logEntry.id)} useSpinner={false} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default IsolatedLogViewer;
